import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit,
  Check,
  Close,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { XiBowlerPhaseProjection } from '../types/lineup';

interface DeploymentRow {
  player_id?: number;
  'Bowler Name': string;
  'Powerplay Overs': number;
  'Middle Overs': number;
  'Slog/Death Overs': number;
  'Total Overs': number;
}

interface TVAAPhaseProj {
  playerId: number;
  bowlerName: string;
  /** Null when that phase projection is missing */
  pp: number | null;
  mo: number | null;
  so: number | null;
  hasPp: boolean;
  hasMo: boolean;
  hasSo: boolean;
  hasAnyPhase: boolean;
  /** True only when all three phases exist */
  hasAllPhases: boolean;
}

interface EditableBowlingDeploymentProps {
  deployment: DeploymentRow[];
  originalTV: number;
  deploymentType: '5' | '6' | '7';
  tvaaPhaseProj?: TVAAPhaseProj[];
  xiBowlerPhaseProj?: XiBowlerPhaseProjection[];
  onUpdate?: (updatedDeployment: DeploymentRow[], updatedTV: number) => void;
  updatedDeployment?: DeploymentRow[];
  updatedTV?: number;
}

// Must match backend phase_overs_needed: pp=6, mo=10, so=4 (sums to 20)
const PHASE_CONSTRAINTS = {
  pp: { max: 6, label: 'PP' },
  mo: { max: 10, label: 'MO' },
  so: { max: 4, label: 'SO/Death' },
};

const BOWLER_CONSTRAINTS = {
  maxTotal: 4,
  minTotal: 0,
};

const TOTAL_OVERS = 20;

type PhaseKey = 'pp' | 'mo' | 'so';
type PhaseColumn = 'Powerplay Overs' | 'Middle Overs' | 'Slog/Death Overs';

const PHASE_COL: Record<PhaseKey, PhaseColumn> = {
  pp: 'Powerplay Overs',
  mo: 'Middle Overs',
  so: 'Slog/Death Overs',
};

const PHASE_LABEL: Record<PhaseKey, string> = {
  pp: 'PP',
  mo: 'MO',
  so: 'SO',
};

const isPresent = (v: number | null | undefined): v is number =>
  v !== null && v !== undefined && !Number.isNaN(Number(v));

const toTvaaEntry = (entry: {
  player_id?: number;
  playerId?: number;
  player_name?: string;
  bowlerName?: string;
  tvaa_pp?: number | null;
  tvaa_mo?: number | null;
  tvaa_so?: number | null;
  pp?: number | null;
  mo?: number | null;
  so?: number | null;
  has_pp?: boolean;
  has_mo?: boolean;
  has_so?: boolean;
  has_any_phase_projections?: boolean;
  has_phase_projections?: boolean;
  hasPhaseProjections?: boolean;
  hasPp?: boolean;
  hasMo?: boolean;
  hasSo?: boolean;
  hasAnyPhase?: boolean;
  hasAllPhases?: boolean;
}): TVAAPhaseProj => {
  const ppRaw = entry.tvaa_pp ?? entry.pp;
  const moRaw = entry.tvaa_mo ?? entry.mo;
  const soRaw = entry.tvaa_so ?? entry.so;

  const hasPp = entry.has_pp ?? entry.hasPp ?? isPresent(ppRaw);
  const hasMo = entry.has_mo ?? entry.hasMo ?? isPresent(moRaw);
  const hasSo = entry.has_so ?? entry.hasSo ?? isPresent(soRaw);
  const hasAnyPhase =
    entry.has_any_phase_projections ?? entry.hasAnyPhase ?? (hasPp || hasMo || hasSo);
  const hasAllPhases =
    entry.has_phase_projections ??
    entry.hasPhaseProjections ??
    entry.hasAllPhases ??
    (hasPp && hasMo && hasSo);

  return {
    playerId: entry.player_id ?? entry.playerId ?? 0,
    bowlerName: entry.player_name ?? entry.bowlerName ?? '',
    pp: hasPp && isPresent(ppRaw) ? Number(ppRaw) : null,
    mo: hasMo && isPresent(moRaw) ? Number(moRaw) : null,
    so: hasSo && isPresent(soRaw) ? Number(soRaw) : null,
    hasPp,
    hasMo,
    hasSo,
    hasAnyPhase,
    hasAllPhases,
  };
};

const phaseAvailable = (proj: TVAAPhaseProj | undefined, phase: PhaseKey): boolean => {
  if (!proj) return false;
  if (phase === 'pp') return proj.hasPp;
  if (phase === 'mo') return proj.hasMo;
  return proj.hasSo;
};

const phaseRate = (proj: TVAAPhaseProj | undefined, phase: PhaseKey): number | null => {
  if (!proj || !phaseAvailable(proj, phase)) return null;
  if (phase === 'pp') return proj.pp;
  if (phase === 'mo') return proj.mo;
  return proj.so;
};

const missingPhasesForProj = (proj: TVAAPhaseProj | undefined): PhaseKey[] => {
  if (!proj) return ['pp', 'mo', 'so'];
  const missing: PhaseKey[] = [];
  if (!proj.hasPp) missing.push('pp');
  if (!proj.hasMo) missing.push('mo');
  if (!proj.hasSo) missing.push('so');
  return missing;
};

const availablePhaseLabels = (proj: TVAAPhaseProj | undefined): string => {
  if (!proj) return 'none';
  const parts: string[] = [];
  if (proj.hasPp) parts.push('PP');
  if (proj.hasMo) parts.push('MO');
  if (proj.hasSo) parts.push('SO');
  return parts.length ? parts.join(', ') : 'none';
};

const EditableBowlingDeployment: React.FC<EditableBowlingDeploymentProps> = ({
  deployment,
  originalTV,
  deploymentType,
  tvaaPhaseProj = [],
  xiBowlerPhaseProj = [],
  onUpdate,
  updatedDeployment,
  updatedTV: parentUpdatedTV,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDeployment, setEditedDeployment] = useState<DeploymentRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [warnings, setWarnings] = useState<string[]>([]);
  const [updatedTV, setUpdatedTV] = useState<number | null>(null);
  const [showOriginalDeployment, setShowOriginalDeployment] = useState(false);

  const mergedPhaseProj = useMemo(() => {
    const byId = new Map<number, TVAAPhaseProj>();
    // Normalize both sources so null phases stay null (never coerce missing → 0)
    tvaaPhaseProj.forEach((p) => byId.set(p.playerId, toTvaaEntry(p)));
    xiBowlerPhaseProj.forEach((p) => byId.set(p.player_id, toTvaaEntry(p)));
    return Array.from(byId.values());
  }, [tvaaPhaseProj, xiBowlerPhaseProj]);

  const getProjForPlayer = useCallback(
    (playerId?: number, bowlerName?: string): TVAAPhaseProj | undefined => {
      if (playerId) {
        const byId = mergedPhaseProj.find((p) => p.playerId === playerId);
        if (byId) return byId;
      }
      if (bowlerName) {
        return mergedPhaseProj.find(
          (p) => p.bowlerName.toLowerCase() === bowlerName.toLowerCase()
        );
      }
      return undefined;
    },
    [mergedPhaseProj]
  );

  const calculateTVForDeployment = useCallback(
    (deploymentData: DeploymentRow[], reason = 'recalc'): number => {
      console.group(
        `[BowlingTV] ${deploymentType}-bowler deployment calc (${reason})`
      );

      if (mergedPhaseProj.length === 0) {
        console.warn(
          '[BowlingTV] No phase projections available — returning originalTV:',
          originalTV
        );
        console.groupEnd();
        return originalTV;
      }

      let totalTV = 0;
      const rowBreakdown: Array<Record<string, unknown>> = [];

      deploymentData.forEach((row) => {
        const bowlerName = row['Bowler Name'];
        const playerId = row.player_id;
        const bowlerProj = getProjForPlayer(playerId, bowlerName);
        const ppOvers = Number(row['Powerplay Overs']) || 0;
        const moOvers = Number(row['Middle Overs']) || 0;
        const soOvers = Number(row['Slog/Death Overs']) || 0;

        if (bowlerProj && bowlerProj.hasAnyPhase) {
          const ppRate = phaseRate(bowlerProj, 'pp');
          const moRate = phaseRate(bowlerProj, 'mo');
          const soRate = phaseRate(bowlerProj, 'so');
          const ppTV = ppRate !== null ? ppOvers * ppRate : 0;
          const moTV = moRate !== null ? moOvers * moRate : 0;
          const soTV = soRate !== null ? soOvers * soRate : 0;
          const rowTV = ppTV + moTV + soTV;
          totalTV += rowTV;

          const detail = {
            player_id: playerId,
            bowler: bowlerName,
            overs: { pp: ppOvers, mo: moOvers, so: soOvers, total: ppOvers + moOvers + soOvers },
            tvaa_phase: { pp: ppRate, mo: moRate, so: soRate },
            available_phases: availablePhaseLabels(bowlerProj),
            tv_contrib: {
              pp: ppRate !== null ? `${ppOvers} × ${ppRate} = ${ppTV}` : 'n/a (no PP proj)',
              mo: moRate !== null ? `${moOvers} × ${moRate} = ${moTV}` : 'n/a (no MO proj)',
              so: soRate !== null ? `${soOvers} × ${soRate} = ${soTV}` : 'n/a (no SO proj)',
              row_tv: rowTV,
            },
          };
          rowBreakdown.push(detail);
          console.log(
            `[BowlingTV] ${bowlerName} (id=${playerId}) [${availablePhaseLabels(bowlerProj)}]: ` +
              `PP ${ppRate !== null ? `${ppOvers}*${ppRate}=${ppTV.toFixed(4)}` : 'n/a'}, ` +
              `MO ${moRate !== null ? `${moOvers}*${moRate}=${moTV.toFixed(4)}` : 'n/a'}, ` +
              `SO ${soRate !== null ? `${soOvers}*${soRate}=${soTV.toFixed(4)}` : 'n/a'} → row=${rowTV.toFixed(4)}`
          );
        } else {
          const detail = {
            player_id: playerId,
            bowler: bowlerName,
            overs: { pp: ppOvers, mo: moOvers, so: soOvers },
            tvaa_phase: bowlerProj
              ? { pp: bowlerProj.pp, mo: bowlerProj.mo, so: bowlerProj.so }
              : null,
            has_any_phase: false,
            skipped: true,
            reason: 'no phase projections',
          };
          rowBreakdown.push(detail);
          console.warn(
            `[BowlingTV] SKIPPED ${bowlerName} (id=${playerId}) — no phase projections`,
            detail
          );
        }
      });

      console.log('[BowlingTV] Row breakdown:', rowBreakdown);
      console.log(
        `[BowlingTV] Total ${deploymentType}-bowler TV:`,
        totalTV,
        `(original was ${originalTV})`
      );
      console.groupEnd();
      return totalTV;
    },
    [mergedPhaseProj, getProjForPlayer, originalTV, deploymentType]
  );

  /**
   * Zero overs in phases the bowler cannot bowl, and redistribute those overs
   * to other deployment bowlers who have that phase + spare capacity (max 4).
   */
  const stripAndRedistributeOvers = useCallback(
    (
      dep: DeploymentRow[],
      swappedIndex: number
    ): { deployment: DeploymentRow[]; notes: string[]; failed: string[] } => {
      const next = dep.map((r) => ({ ...r }));
      const swapped = next[swappedIndex];
      const proj = getProjForPlayer(swapped.player_id, swapped['Bowler Name']);
      const notes: string[] = [];
      const failed: string[] = [];

      const phasesToStrip: PhaseKey[] =
        !proj || !proj.hasAnyPhase ? ['pp', 'mo', 'so'] : missingPhasesForProj(proj);

      phasesToStrip.forEach((phase) => {
        const col = PHASE_COL[phase];
        const amount = Number(swapped[col]) || 0;
        if (amount <= 0) return;

        swapped[col] = 0;
        let remaining = amount;

        const candidates = next
          .map((row, i) => ({ row, i }))
          .filter(({ i, row }) => {
            if (i === swappedIndex) return false;
            const p = getProjForPlayer(row.player_id, row['Bowler Name']);
            if (!phaseAvailable(p, phase)) return false;
            const total =
              Number(row['Powerplay Overs']) +
              Number(row['Middle Overs']) +
              Number(row['Slog/Death Overs']);
            return total < BOWLER_CONSTRAINTS.maxTotal - 0.001;
          })
          .sort((a, b) => {
            const totA =
              Number(a.row['Powerplay Overs']) +
              Number(a.row['Middle Overs']) +
              Number(a.row['Slog/Death Overs']);
            const totB =
              Number(b.row['Powerplay Overs']) +
              Number(b.row['Middle Overs']) +
              Number(b.row['Slog/Death Overs']);
            return totA - totB;
          });

        for (const { row } of candidates) {
          if (remaining <= 0.001) break;
          const total =
            Number(row['Powerplay Overs']) +
            Number(row['Middle Overs']) +
            Number(row['Slog/Death Overs']);
          const capacity = Math.max(0, BOWLER_CONSTRAINTS.maxTotal - total);
          if (capacity <= 0) continue;
          const give = Math.min(remaining, capacity);
          row[col] = Number(row[col]) + give;
          remaining -= give;
        }

        if (remaining > 0.001) {
          failed.push(
            `${PHASE_LABEL[phase]}: ${remaining.toFixed(1)} of ${amount.toFixed(1)} overs from ${swapped['Bowler Name']} could not be reassigned (others lack ${PHASE_LABEL[phase]} proj or are at 4 overs)`
          );
        } else {
          notes.push(
            `Moved ${amount.toFixed(1)} ${PHASE_LABEL[phase]} overs from ${swapped['Bowler Name']} (no ${PHASE_LABEL[phase]} projection)`
          );
        }
      });

      next.forEach((row) => {
        row['Total Overs'] =
          Number(row['Powerplay Overs']) +
          Number(row['Middle Overs']) +
          Number(row['Slog/Death Overs']);
      });

      return { deployment: next, notes, failed };
    },
    [getProjForPlayer]
  );

  const collectProjectionWarnings = useCallback(
    (deploymentData: DeploymentRow[]): string[] => {
      const msgs: string[] = [];
      deploymentData.forEach((row) => {
        const proj = getProjForPlayer(row.player_id, row['Bowler Name']);
        if (!proj || !proj.hasAnyPhase) {
          msgs.push(
            `${row['Bowler Name']} has no phase bowling projections — TV contribution is 0.`
          );
          return;
        }
        if (!proj.hasAllPhases) {
          msgs.push(
            `${row['Bowler Name']} has partial projections (${availablePhaseLabels(proj)} only) — overs only count in those phases.`
          );
        }
        (['pp', 'mo', 'so'] as PhaseKey[]).forEach((phase) => {
          const overs = Number(row[PHASE_COL[phase]]) || 0;
          if (overs > 0.01 && !phaseAvailable(proj, phase)) {
            msgs.push(
              `${row['Bowler Name']} still has ${overs.toFixed(1)} ${PHASE_LABEL[phase]} overs without a ${PHASE_LABEL[phase]} projection — reassign or set to 0.`
            );
          }
        });
      });
      return msgs;
    },
    [getProjForPlayer]
  );

  const getSwapOptionsForRow = useCallback(
    (rowIndex: number): XiBowlerPhaseProjection[] => {
      const usedIds = new Set(
        editedDeployment
          .map((row, idx) => (idx === rowIndex ? null : row.player_id))
          .filter((id): id is number => id != null && id !== 0)
      );

      // Prefer players with any phase proj; XI first, impact last
      return xiBowlerPhaseProj
        .filter((p) => !usedIds.has(p.player_id))
        .filter((p) => {
          const mapped = toTvaaEntry(p);
          return mapped.hasAnyPhase || p.player_id === editedDeployment[rowIndex]?.player_id;
        })
        .sort((a, b) => Number(!!a.is_impact) - Number(!!b.is_impact));
    },
    [editedDeployment, xiBowlerPhaseProj]
  );

  const formatSwapLabel = useCallback(
    (
      p:
        | XiBowlerPhaseProjection
        | {
            player_name?: string;
            'Bowler Name'?: string;
            is_impact?: boolean;
            has_phase_projections?: boolean;
            has_any_phase_projections?: boolean;
            has_pp?: boolean;
            has_mo?: boolean;
            has_so?: boolean;
          },
      isCurrent = false
    ) => {
      const name =
        'player_name' in p && p.player_name
          ? p.player_name
          : (p as { 'Bowler Name'?: string })['Bowler Name'] ?? '';
      const mapped = toTvaaEntry(p as XiBowlerPhaseProjection);
      const bits: string[] = [];
      if (p.is_impact) bits.push('Impact');
      if (!mapped.hasAnyPhase) {
        bits.push('no projections');
      } else if (!mapped.hasAllPhases) {
        bits.push(`partial: ${availablePhaseLabels(mapped)}`);
      }
      if (isCurrent && bits.length === 0) return name;
      return bits.length ? `${name} (${bits.join(', ')})` : name;
    },
    []
  );

  useEffect(() => {
    if (isEditing) {
      const baseDeployment =
        updatedDeployment && updatedDeployment.length > 0
          ? updatedDeployment
          : deployment;

      const initialEdited = baseDeployment.map((row) => ({ ...row }));
      setEditedDeployment(initialEdited);
      setErrors({});
      setWarnings(collectProjectionWarnings(initialEdited));
      setUpdatedTV(calculateTVForDeployment(initialEdited, 'enter-edit'));
      setShowOriginalDeployment(false);
    } else {
      setShowOriginalDeployment(false);
      setWarnings([]);
    }
  }, [
    isEditing,
    deployment,
    updatedDeployment,
    calculateTVForDeployment,
    collectProjectionWarnings,
  ]);

  const validateDeployment = (): boolean => {
    const newErrors: Record<string, string> = {};

    let ppTotal = 0;
    let moTotal = 0;
    let soTotal = 0;
    let totalOvers = 0;

    editedDeployment.forEach((row, idx) => {
      const pp = row['Powerplay Overs'];
      const mo = row['Middle Overs'];
      const so = row['Slog/Death Overs'];
      const total = pp + mo + so;

      if (total > BOWLER_CONSTRAINTS.maxTotal) {
        newErrors[`${idx}-total`] = `Max ${BOWLER_CONSTRAINTS.maxTotal} overs per bowler`;
      }
      if (total < BOWLER_CONSTRAINTS.minTotal) {
        newErrors[`${idx}-total`] = `Min ${BOWLER_CONSTRAINTS.minTotal} overs per bowler`;
      }
      if (pp < 0 || mo < 0 || so < 0) {
        newErrors[`${idx}-negative`] = 'Overs cannot be negative';
      }

      const proj = getProjForPlayer(row.player_id, row['Bowler Name']);
      if (!proj || !proj.hasAnyPhase) {
        if (total > 0.01) {
          newErrors[`${idx}-proj`] =
            `${row['Bowler Name']} has no phase bowling projections and cannot keep overs`;
        }
      } else {
        (['pp', 'mo', 'so'] as PhaseKey[]).forEach((phase) => {
          const overs = Number(row[PHASE_COL[phase]]) || 0;
          if (overs > 0.01 && !phaseAvailable(proj, phase)) {
            newErrors[`${idx}-${phase}`] =
              `${row['Bowler Name']} has ${overs} ${PHASE_LABEL[phase]} overs but no ${PHASE_LABEL[phase]} projection`;
          }
        });
      }

      ppTotal += pp;
      moTotal += mo;
      soTotal += so;
      totalOvers += total;
    });

    if (Math.abs(ppTotal - PHASE_CONSTRAINTS.pp.max) > 0.01) {
      newErrors['pp-total'] = `PP must total ${PHASE_CONSTRAINTS.pp.max} overs (currently ${ppTotal})`;
    }
    if (Math.abs(moTotal - PHASE_CONSTRAINTS.mo.max) > 0.01) {
      newErrors['mo-total'] = `MO must total ${PHASE_CONSTRAINTS.mo.max} overs (currently ${moTotal})`;
    }
    if (Math.abs(soTotal - PHASE_CONSTRAINTS.so.max) > 0.01) {
      newErrors['so-total'] = `SO/Death must total ${PHASE_CONSTRAINTS.so.max} overs (currently ${soTotal})`;
    }
    if (Math.abs(totalOvers - TOTAL_OVERS) > 0.01) {
      newErrors['total-overs'] = `Total must be ${TOTAL_OVERS} overs (currently ${totalOvers})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const refreshTvAndWarnings = (updated: DeploymentRow[], reason = 'recalc') => {
    setUpdatedTV(calculateTVForDeployment(updated, reason));
    setWarnings(collectProjectionWarnings(updated));
  };

  const handleCellChange = (
    rowIndex: number,
    phase: 'Powerplay Overs' | 'Middle Overs' | 'Slog/Death Overs',
    value: string
  ) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0) return;

    const row = editedDeployment[rowIndex];
    const proj = getProjForPlayer(row?.player_id, row?.['Bowler Name']);
    const phaseKey: PhaseKey =
      phase === 'Powerplay Overs' ? 'pp' : phase === 'Middle Overs' ? 'mo' : 'so';
    if (numValue > 0 && !phaseAvailable(proj, phaseKey)) {
      setErrors((prev) => ({
        ...prev,
        [`${rowIndex}-${phaseKey}`]: `${row['Bowler Name']} has no ${PHASE_LABEL[phaseKey]} projection`,
      }));
      return;
    }

    const updated = editedDeployment.map((r, idx) => {
      if (idx === rowIndex) {
        const updatedRow = { ...r };
        updatedRow[phase] = numValue;
        updatedRow['Total Overs'] =
          updatedRow['Powerplay Overs'] +
          updatedRow['Middle Overs'] +
          updatedRow['Slog/Death Overs'];
        return updatedRow;
      }
      return r;
    });

    setEditedDeployment(updated);
    setErrors({});
    refreshTvAndWarnings(updated, `overs-edit:${phase}`);
  };

  const handleBowlerChange = (rowIndex: number, newPlayerId: number) => {
    const swapPlayer = xiBowlerPhaseProj.find((p) => p.player_id === newPlayerId);
    if (!swapPlayer) return;

    const oldRow = editedDeployment[rowIndex];
    const oldProj = getProjForPlayer(oldRow?.player_id, oldRow?.['Bowler Name']);
    const newMapped = toTvaaEntry(swapPlayer);

    const swapped = editedDeployment.map((row, idx) => {
      if (idx !== rowIndex) return row;
      return {
        ...row,
        player_id: swapPlayer.player_id,
        'Bowler Name': swapPlayer.player_name,
      };
    });

    const { deployment: rebalanced, notes, failed } = stripAndRedistributeOvers(
      swapped,
      rowIndex
    );

    console.group(
      `[BowlerSwap] ${deploymentType}-bowler: ` +
        `${oldRow?.['Bowler Name'] ?? '?'} → ${swapPlayer.player_name}`
    );
    console.log('[BowlerSwap] Replaced player:', {
      from: {
        player_id: oldRow?.player_id,
        name: oldRow?.['Bowler Name'],
        overs: {
          pp: oldRow?.['Powerplay Overs'],
          mo: oldRow?.['Middle Overs'],
          so: oldRow?.['Slog/Death Overs'],
          total: oldRow?.['Total Overs'],
        },
        tvaa_phase: oldProj
          ? { pp: oldProj.pp, mo: oldProj.mo, so: oldProj.so }
          : null,
      },
      to: {
        player_id: swapPlayer.player_id,
        name: swapPlayer.player_name,
        is_impact: !!swapPlayer.is_impact,
        available_phases: availablePhaseLabels(newMapped),
        overs_after_rebalance: {
          pp: rebalanced[rowIndex]?.['Powerplay Overs'],
          mo: rebalanced[rowIndex]?.['Middle Overs'],
          so: rebalanced[rowIndex]?.['Slog/Death Overs'],
          total: rebalanced[rowIndex]?.['Total Overs'],
        },
        tvaa_phase: {
          pp: newMapped.pp,
          mo: newMapped.mo,
          so: newMapped.so,
        },
        has_any_phase: newMapped.hasAnyPhase,
        has_all_phases: newMapped.hasAllPhases,
        redistribute_notes: notes,
        redistribute_failed: failed,
      },
    });
    console.groupEnd();

    setEditedDeployment(rebalanced);
    setErrors({});
    refreshTvAndWarnings(rebalanced, 'bowler-swap');

    if (notes.length > 0 || failed.length > 0) {
      setWarnings((prev) => [...prev, ...notes, ...failed]);
    }
  };

  const handleApply = () => {
    if (!validateDeployment()) {
      return;
    }

    const newTV = calculateTVForDeployment(editedDeployment, 'apply');
    setUpdatedTV(newTV);
    console.log(
      `[BowlingTV] Applied ${deploymentType}-bowler deployment. Final TV:`,
      newTV
    );

    if (onUpdate) {
      onUpdate(editedDeployment, newTV);
    }

    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedDeployment([]);
    setErrors({});
    setWarnings([]);
    setUpdatedTV(null);
  };

  const calculatePhaseTotals = (deploymentData: DeploymentRow[]) =>
    deploymentData.reduce(
      (acc, row) => {
        acc.pp += row['Powerplay Overs'];
        acc.mo += row['Middle Overs'];
        acc.so += row['Slog/Death Overs'];
        acc.total += row['Total Overs'];
        return acc;
      },
      { pp: 0, mo: 0, so: 0, total: 0 }
    );

  const originalTotals = calculatePhaseTotals(deployment);
  const editedTotals = isEditing ? calculatePhaseTotals(editedDeployment) : originalTotals;

  const hasUpdatedDeployment = updatedDeployment && updatedDeployment.length > 0;
  const updatedTotals = hasUpdatedDeployment ? calculatePhaseTotals(updatedDeployment) : null;
  const displayUpdatedTV =
    parentUpdatedTV !== undefined ? parentUpdatedTV : updatedTV !== null ? updatedTV : null;

  const canSwapBowlers = xiBowlerPhaseProj.length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontWeight: 'bold' }}>
          Bowling Deployment Strategy ({deploymentType} Bowlers)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isEditing ? (
            <Tooltip title="Edit Deployment">
              <IconButton size="small" onClick={() => setIsEditing(true)} color="primary">
                <Edit />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Tooltip
                title={
                  Object.keys(errors).length > 0
                    ? 'Fix validation errors to apply changes'
                    : 'Apply Changes'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleApply}
                    color="primary"
                    disabled={Object.keys(errors).length > 0}
                  >
                    <Check />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={handleCancel} color="error">
                  <Close />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {warnings.length > 0 && isEditing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warnings.map((warning, idx) => (
            <div key={idx}>{warning}</div>
          ))}
        </Alert>
      )}

      {Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {Object.values(errors).map((error, idx) => (
            <div key={idx}>{error}</div>
          ))}
        </Alert>
      )}

      {isEditing ? (
        <>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Editing Deployment
              </Typography>
              {updatedTV !== null && (
                <Chip label={`Updated TV: ${updatedTV.toFixed(2)}`} color="primary" size="small" />
              )}
            </Box>
            {!canSwapBowlers && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Bowler swap unavailable — re-evaluate the lineup to load XI / impact phase projections.
              </Typography>
            )}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Bowler</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      PP
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      MO
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      SO/Death
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editedDeployment.map((row, idx) => {
                    const swapOptions = getSwapOptionsForRow(idx);
                    const rowProj = getProjForPlayer(row.player_id, row['Bowler Name']);
                    const canEditPp =
                      phaseAvailable(rowProj, 'pp') || Number(row['Powerplay Overs']) > 0;
                    const canEditMo =
                      phaseAvailable(rowProj, 'mo') || Number(row['Middle Overs']) > 0;
                    const canEditSo =
                      phaseAvailable(rowProj, 'so') || Number(row['Slog/Death Overs']) > 0;
                    const ppLocked = !phaseAvailable(rowProj, 'pp');
                    const moLocked = !phaseAvailable(rowProj, 'mo');
                    const soLocked = !phaseAvailable(rowProj, 'so');
                    return (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>
                          {canSwapBowlers && swapOptions.length > 0 ? (
                            <FormControl size="small" fullWidth error={!!errors[`${idx}-proj`]}>
                              <Select
                                value={row.player_id ?? ''}
                                onChange={(e) =>
                                  handleBowlerChange(idx, Number(e.target.value))
                                }
                                displayEmpty
                              >
                                {row.player_id && (
                                  <MenuItem value={row.player_id}>
                                    {formatSwapLabel({
                                      player_name: row['Bowler Name'],
                                      is_impact: xiBowlerPhaseProj.find(
                                        (p) => p.player_id === row.player_id
                                      )?.is_impact,
                                      has_pp: rowProj?.hasPp,
                                      has_mo: rowProj?.hasMo,
                                      has_so: rowProj?.hasSo,
                                      has_any_phase_projections: rowProj?.hasAnyPhase,
                                      has_phase_projections: rowProj?.hasAllPhases,
                                    })}
                                  </MenuItem>
                                )}
                                {swapOptions
                                  .filter((p) => p.player_id !== row.player_id)
                                  .map((p) => (
                                    <MenuItem key={p.player_id} value={p.player_id}>
                                      {formatSwapLabel(p)}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          ) : (
                            row['Bowler Name']
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              ppLocked
                                ? `${row['Bowler Name']} has no PP projection — set overs to 0 or reassign`
                                : ''
                            }
                          >
                            <span>
                              <TextField
                                type="number"
                                value={row['Powerplay Overs']}
                                onChange={(e) =>
                                  handleCellChange(idx, 'Powerplay Overs', e.target.value)
                                }
                                size="small"
                                disabled={!canEditPp}
                                inputProps={{
                                  min: 0,
                                  step: 1,
                                  style: { textAlign: 'center', width: 60 },
                                }}
                                error={
                                  !!errors[`${idx}-total`] ||
                                  !!errors[`${idx}-negative`] ||
                                  !!errors[`${idx}-pp`] ||
                                  !!errors['pp-total']
                                }
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              moLocked
                                ? `${row['Bowler Name']} has no MO projection — set overs to 0 or reassign`
                                : ''
                            }
                          >
                            <span>
                              <TextField
                                type="number"
                                value={row['Middle Overs']}
                                onChange={(e) =>
                                  handleCellChange(idx, 'Middle Overs', e.target.value)
                                }
                                size="small"
                                disabled={!canEditMo}
                                inputProps={{
                                  min: 0,
                                  step: 1,
                                  style: { textAlign: 'center', width: 60 },
                                }}
                                error={
                                  !!errors[`${idx}-total`] ||
                                  !!errors[`${idx}-negative`] ||
                                  !!errors[`${idx}-mo`] ||
                                  !!errors['mo-total']
                                }
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              soLocked
                                ? `${row['Bowler Name']} has no SO projection — set overs to 0 or reassign`
                                : ''
                            }
                          >
                            <span>
                              <TextField
                                type="number"
                                value={row['Slog/Death Overs']}
                                onChange={(e) =>
                                  handleCellChange(idx, 'Slog/Death Overs', e.target.value)
                                }
                                size="small"
                                disabled={!canEditSo}
                                inputProps={{
                                  min: 0,
                                  step: 1,
                                  style: { textAlign: 'center', width: 60 },
                                }}
                                error={
                                  !!errors[`${idx}-total`] ||
                                  !!errors[`${idx}-negative`] ||
                                  !!errors[`${idx}-so`] ||
                                  !!errors['so-total']
                                }
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {row['Total Overs']}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {editedTotals.pp}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {editedTotals.mo}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {editedTotals.so}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {editedTotals.total}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                p: 1,
                borderRadius: 1,
              }}
              onClick={() => setShowOriginalDeployment(!showOriginalDeployment)}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOriginalDeployment(!showOriginalDeployment);
                }}
              >
                {showOriginalDeployment ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flex: 1 }}>
                Original Deployment (Reference)
              </Typography>
              <Chip label={`TV: ${originalTV.toFixed(2)}`} color="default" size="small" />
            </Box>
            {showOriginalDeployment && (
              <TableContainer component={Paper} sx={{ bgcolor: 'grey.50' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Bowler</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        PP
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        MO
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        SO/Death
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deployment.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{row['Bowler Name']}</TableCell>
                        <TableCell align="center">{row['Powerplay Overs']}</TableCell>
                        <TableCell align="center">{row['Middle Overs']}</TableCell>
                        <TableCell align="center">{row['Slog/Death Overs']}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {row['Total Overs']}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ mb: hasUpdatedDeployment ? 3 : 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                p: 1,
                borderRadius: 1,
              }}
              onClick={() => setShowOriginalDeployment(!showOriginalDeployment)}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOriginalDeployment(!showOriginalDeployment);
                }}
              >
                {showOriginalDeployment ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flex: 1 }}>
                Original Deployment
              </Typography>
              <Chip label={`TV: ${originalTV.toFixed(2)}`} color="default" size="small" />
            </Box>
            {showOriginalDeployment && (
              <TableContainer component={Paper} sx={{ bgcolor: 'grey.50' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Bowler</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        PP
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        MO
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        SO/Death
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deployment.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{row['Bowler Name']}</TableCell>
                        <TableCell align="center">{row['Powerplay Overs']}</TableCell>
                        <TableCell align="center">{row['Middle Overs']}</TableCell>
                        <TableCell align="center">{row['Slog/Death Overs']}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {row['Total Overs']}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {hasUpdatedDeployment && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Updated Deployment
                </Typography>
                <Chip label="Updated" color="primary" size="small" />
                {displayUpdatedTV !== null && (
                  <Chip
                    label={`TV: ${displayUpdatedTV.toFixed(2)}`}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              <TableContainer component={Paper} sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Bowler</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        PP
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        MO
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        SO/Death
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {updatedDeployment!.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{row['Bowler Name']}</TableCell>
                        <TableCell align="center">{row['Powerplay Overs']}</TableCell>
                        <TableCell align="center">{row['Middle Overs']}</TableCell>
                        <TableCell align="center">{row['Slog/Death Overs']}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {row['Total Overs']}
                        </TableCell>
                      </TableRow>
                    ))}
                    {updatedTotals && (
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {updatedTotals.pp}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {updatedTotals.mo}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {updatedTotals.so}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {updatedTotals.total}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default EditableBowlingDeployment;
